import React, { useEffect, useState, useContext, useRef } from 'react';
import { Auth } from 'aws-amplify';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { Avatar } from 'primereact/avatar';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Menu } from 'primereact/menu';
import { Tooltip } from 'primereact/tooltip';
import Cookies from 'universal-cookie';
import classNames from 'classnames';
import { appVersion } from 'config/app_version';

import { UserDetailsContext } from 'context/userDetailsContext';
import { WebSocketConnectionContext } from 'context/WebSocketConnectionContext';
import { notificationAction } from 'redux/actions/notificationAction';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { markNotification, clearNotifications, resetNotificationCount } from 'services/notification/notificationService';
import { firmSelectHandler, logout, openLinkInBlank } from 'utils/utils';
import useSpinner from 'hooks/useSpinner';

import { getUserProfileDetails } from 'services/profileServices';
import { presignedURLGet } from 'modules/file_manager/services';
import { loginLogoutEvent } from 'services/generalServices';
import { useNavbarContext } from 'context/NavbarContext';
import { default as InfiniteScrollList } from 'components/InfiniteScrollNotificationList/InfiniteScrollNotificationList';
import { Notification } from 'modules/layout/components/Notification/Notification';
import { NotificationPopup } from 'components/NotificationPopup/NotificationPopup';
import logo from '../../assets/images/Full_Logo_Black.svg';
import logodark from '../../assets/images/Full_Logo_White.svg';
import constants from 'constants/index';
import useDynamicHeight from 'components/UI/NavbarHeight/useDynamicHeight';

const cookie = new Cookies();

export const UserProfileContext = React.createContext();

const Layout = ({ children, showSideBar }) => {
  const history = useHistory();
  const [userInfo, setUserInfo] = useState('');
  const profileRef = useRef(null);
  const [navigationToggle, setNavigationToggle] = useState(false);
  const userContext = useContext(UserDetailsContext);
  const { notificationCounts, getNotificationCounts, notificationData } = useNavbarContext(); //here
  const { notifications = [], next_token: nextToken, total_notifications } = notificationData?.notifications || {};

  const { isLoading, setNotifications } = notificationData || {};

  const location = useLocation();
  const { pathname } = location;
  const [newNotification, setNewNotification] = useState(false);
  const notificationPanel = useRef();
  const websocketContext = useContext(WebSocketConnectionContext);
  const { addToast } = useToast();
  const dispatch = useDispatch();
  const [spinner, showSpinner, hideSpinner] = useSpinner(true);
  const [imageUrl, setImageUrl] = useState('');
  const [currentToken, setCurrentToken] = useState('');
  const [webSocketNotificationsCount, setWebSocketNotificationsCount] = useState(0);
  const [showNotificationPopup, setShowNotificationPopup] = useState(false);
  const [notificationPopupData, setNotificationPopupData] = useState(null);

  // Use sessionStorage to persist shown notifications across navigations
  const getShownNotifications = () => {
    try {
      const stored = sessionStorage.getItem('shownNotifications_client');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  };

  const addShownNotification = (id) => {
    try {
      const shown = getShownNotifications();
      shown.add(id);
      sessionStorage.setItem('shownNotifications_client', JSON.stringify([...shown]));
    } catch (error) {
      console.error('[Client] Error saving shown notification:', error);
    }
  };

  const loggedInUserId = cookie.get('userId') || null;
  const totalNotiicationsCount = Number(total_notifications || 0) + Number(webSocketNotificationsCount || 0) || 0;
  const { dynamicHeight } = useDynamicHeight();

  const clearAll = (e) => {
    e.preventDefault();
    showSpinner();
    clearNotifications()
      .then(() => {
        hideSpinner();
        setWebSocketNotificationsCount(0);
        setNotifications({});
      })
      .catch((err) => {
        hideSpinner();
      });
  };

  const handleLogout = () => {
    try {
      loginLogoutEvent();
      Auth.signOut()
        .then(() => {
          logout(history);
          websocketContext.closeConnection();
        })
        .catch((error) => {});
    } catch (e) {}
  };

  const markAsRead = (e, data) => {
    showSpinner();
    e.preventDefault();
    markNotification(true, [data?.sk])
      .then(() => {
        notificationData?.markasRead(data?.sk);
        notificationData?.notifications?.notifications?.length <= 8 &&
          nextToken &&
          notificationData?.loadNotifications({ notificationType: '', limit: 10, nextToken });
        hideSpinner();
      })
      .catch((err) => {
        console.log('err', err);
        hideSpinner();
      });
  };

  const profileMenu = [
    {
      label: 'Profile',
      url: '/#/client/profile',
      command: () => {
        if (!openLinkInBlank('/client/profile')) {
          window.location.hash = '/client/profile';
          setNavigationToggle(false);
        }
      },
      show: true,
    },
    {
      label: `Select Firm`,
      command: () => firmSelectHandler(history),
      show: userContext?.firmDetails?.firm_details?.length > 1,
    },
    {
      label: `Logout`,
      command: handleLogout,
      show: true,
    },
  ];

  const hideDropdown = (e) => setNavigationToggle(false);

  const fetchImage = async (filePath) => {
    try {
      let fileSize = 'small.jpg';
      if (filePath) {
        let proURL = filePath;
        let url = proURL.split('/');
        url.pop();
        if (fileSize) {
          url.push(fileSize);
        } else {
          url.push('small.jpg');
        }
        let finalUrl = url.join('/');
        let data = await presignedURLGet(finalUrl, false, true);
        if (data && data.data && data.data.file_url) {
          setImageUrl(data.data.file_url);
        } else {
          setImageUrl('');
        }
      }
    } catch (error) {}
  };

  const fetchUserDetails = () => {
    getUserProfileDetails()
      .then((res) => {
        if (res && res.data) {
          fetchImage(res?.data?.profile_image);
        }
      })
      .catch((error) => console.log(error));
  };
  const getUserDetails = () => {
    try {
      Auth.currentUserInfo()
        .then((res) => {
          localStorage.setItem('userInfo', JSON.stringify(res));
          setUserInfo(res);
        })
        .catch((error) => {
          localStorage.setItem('userInfo', null);
        });
    } catch (e) {
      localStorage.setItem('userInfo', null);
    }
  };

  useEffect(() => {
    getUserDetails();
  }, []);

  useEffect(() => {
    if (userContext) {
      fetchUserDetails();
      userContext?.userDetails?.contact_id && getNotificationCounts(userContext?.userDetails?.contact_id);

      if (userContext?.access_level) {
        let msg = userContext?.selectedPlanDetails?.message;
        if (msg === constants?.NON_FIRM_ADMIN_SUBSCRIPTION_CANCEL_MESSAGE) {
          handleLogout();
        } else {
          let tenantId = cookie.get('tenantId');
          if (userContext?.firmDetails?.current_user && tenantId) {
            // websocketContext?.connectToWebSocket(tenantId, userContext?.firmDetails?.current_user, 'client');
            dispatch(notificationAction('', 1000, ''));
          } else if (!tenantId) {
            return history.replace('/tenant-selection');
          }
        }
      }
    }
  }, [userContext]);

  useEffect(() => {
    let notificationsArr = [...notifications] || [];

    if (websocketContext?.notificationData) {
      const notification = websocketContext.notificationData;
      const notificationId = notification?.sk;

      console.log('🔔 [Client] Notification received:', notification);
      console.log('📊 [Client] Toast flag:', notification?.toast);
      console.log('🪟 [Client] Popup flag:', notification?.popup);
      console.log('📱 [Client] InApp flag:', notification?.inapp);

      if (
        websocketContext?.notificationData?.notification_type === 'secure_message' &&
        websocketContext?.notificationData?.created_by_user_id &&
        websocketContext?.notificationData?.created_by_user_id === loggedInUserId
      ) {
        return;
      }

      // Add to notification list unless inapp is explicitly false
      if (notification?.inapp !== false) {
        setWebSocketNotificationsCount(webSocketNotificationsCount + 1);
        notificationsArr.unshift(websocketContext?.notificationData);
        setNewNotification(true);
        userContext?.userDetails?.contact_id && getNotificationCounts(userContext?.userDetails?.contact_id);
      }

      // Check if we've already shown toast/popup for this notification
      const shownNotifications = getShownNotifications();
      if (notificationId && !shownNotifications.has(notificationId)) {
        // Handle toast notification (1 minute = 60000ms auto-close)
        if (notification?.toast) {
          console.log('🍞 [Client] Showing toast notification');
          addToast(false, toastConstant.toasterType.INFO, toastConstant.api.SUCCESS, '', 'bottom-right', true, notification, 60000);
        }

        // Handle popup notification
        if (notification?.popup) {
          console.log('🪟 [Client] Showing popup notification');
          setNotificationPopupData(notification);
          setShowNotificationPopup(true);
        }

        // Mark this notification as shown and clear from context
        if (notification?.toast || notification?.popup) {
          addShownNotification(notificationId);
          // Clear notification data from websocket context to prevent re-triggering
          setTimeout(() => {
            websocketContext?.setNotificationData(null);
          }, 100);
        }
      } else if (notificationId && shownNotifications.has(notificationId)) {
        console.log('⏭️ [Client] Skipping notification (already shown):', notificationId);
      }
    }

    // Update notification list unless inapp is explicitly false
    if (websocketContext?.notificationData?.inapp !== false) {
      setNotifications((preval) => ({ ...preval, notifications: notificationsArr }));
    }
  }, [websocketContext?.notificationData]);

  if (!showSideBar) return <div id="layout">{children}</div>;

  return (
    <div id="layout" className="h-100 d-flex flex-column">
      {/* Overlay for faded background when navbar is open */}
      {navigationToggle && (
        <div
          className="navbar-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.4)',
            zIndex: 1040,
          }}
          onClick={() => setNavigationToggle(false)}
        />
      )}
      <Notification
        visible={websocketContext.notificationPopup}
        notificationData={websocketContext?.notificationData}
        onHide={() => websocketContext.setNotificationPopup(false)}
      />

      {/* Generic Notification Popup for toast/popup notifications */}
      {showNotificationPopup && notificationPopupData && (
        <NotificationPopup
          visible={showNotificationPopup}
          notificationData={notificationPopupData}
          onHide={() => {
            setShowNotificationPopup(false);
            setNotificationPopupData(null);
          }}
        />
      )}

      <OverlayPanel
        ref={notificationPanel}
        id="overlay_panel"
        style={{ width: '500px' }}
        className="overlaypanel-demo shadow p-1"
        onHide={() => null}
        ariaCloseLabel="notification-panel"
        onContentClick={() => null}
      >
        {spinner}
        <div className="notification-wrapper" style={{ overflow: 'hidden' }}>
          <div className="pb-2">
            <div className="col-12 d-flex justify-content-between align-items-center">
              <h6 className="fw-bold text-dark m-0">Notifications</h6>
              {notifications?.length > 0 && (
                <a href="/" className="text-muted text-decoration-none" onClick={clearAll}>
                  <small>Clear All</small>
                </a>
              )}
            </div>
          </div>

          {notifications?.length > 0 ? (
            <InfiniteScrollList
              isClient
              {...{
                notificationList: notifications,
                loadNotifications: notificationData?.loadNotifications,
                isLoading,
                nextToken,
                currentToken,
                setCurrentToken,
                markAsRead,
              }}
            />
          ) : (
            <div className="row mt-2">
              <div className="col-12 d-flex justify-content-center">No notifications found</div>
            </div>
          )}
        </div>
      </OverlayPanel>
      <nav className="navbar navbar-expand-lg px-2 position-relative" style={{ zIndex: 1050 }}>
        <div className="container-fluid flex-wrap">
          <button
            className="navbar-toggler mx-2"
            type="button"
            data-toggle="collapse"
            data-target="#basicExampleNav"
            aria-controls="basicExampleNav"
            aria-expanded="false"
            aria-label="Toggle navigation"
            onClick={() => setNavigationToggle(!navigationToggle)}
          >
            <span className="navbar-toggler-icon d-flex align-items-center">
              <i className="fa fa-bars"></i>
            </span>
          </button>
          <div className="logo-title-wrap">
            <Link to="/" className="text-dark fw-bold navbar-brand logo-wrap py-0 d-flex align-items-baseline mt-1">
              <img src={document.documentElement.getAttribute('data-theme') === 'dark' ? logodark : logo} alt="logo" width="88" />
            </Link>
            <div className="client-title title gray-800 mb-0 mt-1">{userContext?.tenantName}</div>
          </div>
          <div
            className={navigationToggle ? 'collapse navbar-collapse show' : 'collapse navbar-collapse justify-content-end'}
            id="basicExampleNav"
          >
            <ul className="navbar-nav p-3">
              <li
                className={classNames('nav-item', {
                  active: pathname === '/client/dashboard',
                })}
              >
                <Link to="/" className="nav-link">
                  Dashboard
                </Link>
              </li>
              <li
                className={classNames('nav-item', {
                  active: pathname === '/client/files',
                })}
              >
                <Link to="/client/files" onClick={hideDropdown} className="nav-link">
                  Files
                  {Boolean(notificationCounts?.file_count) && <i className="icon-window-filled pointer F-size12"></i>}
                </Link>
              </li>
              <li
                className={classNames('nav-item', {
                  active: pathname === '/client-forms',
                })}
              >
                <Link to="/client-forms" onClick={hideDropdown} className="nav-link">
                  Forms
                </Link>
              </li>
              {userContext.checkSubPermission('calendaring') && (
                <li
                  className={classNames('nav-item', {
                    active: pathname === '/client/calendar',
                  })}
                >
                  <Link to="/client/calendar" onClick={hideDropdown} className="nav-link">
                    Calendar
                    {Boolean(notificationCounts?.event_count) && <i className="icon-window-filled pointer F-size12"></i>}
                  </Link>
                </li>
              )}
              {userContext.checkSubPermission('task_management') && (
                <li
                  className={classNames('nav-item', {
                    active: pathname === '/client/tasks',
                  })}
                >
                  <Link to="/client/tasks" onClick={hideDropdown} className="nav-link">
                    Tasks
                    {Boolean(notificationCounts?.task_count) && <i className="icon-window-filled pointer F-size12"></i>}
                  </Link>
                </li>
              )}
              {userContext.checkSubPermission('secure_messages') && (
                <li
                  className={classNames('nav-item', {
                    active: pathname === '/client/messages',
                  })}
                >
                  <Link to="/client/messages" onClick={hideDropdown} className="nav-link">
                    Messages
                    {Boolean(notificationCounts?.thread_count) && <i className="icon-window-filled pointer F-size12"></i>}
                  </Link>
                </li>
              )}
              <li
                className={classNames('nav-item', {
                  active: pathname === '/client-billing',
                })}
              >
                <Link to="/client-billing" onClick={hideDropdown} className="nav-link">
                  Billing
                  {Boolean(notificationCounts?.billing_count) && <i className="icon-window-filled pointer F-size12"></i>}
                </Link>
              </li>
              <label className="mt-2 F-size12 bt-inner">
                <div className="d-flex align-items-center justify-content-between mobile-version-footer">
                  <div className="">Version</div>
                  <span className="version-no">v{appVersion}</span>
                </div>
              </label>
            </ul>
          </div>
          <div className="d-flex ms-auto align-items-center">
            <div className="d-flex align-items-center top-nav-avatar">
              <a
                className="nav-link dropdown-toggle d-flex align-items-center"
                id="navbarDropdownMenuLink"
                data-toggle="dropdown"
                aria-expanded="false"
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  profileRef.current.toggle(e);
                }}
              >
                <Avatar
                  image={imageUrl}
                  onImageError={() => setImageUrl('')}
                  className={'me-1'}
                  size={'medium'}
                  shape="circle"
                  imageAlt=""
                  label={!imageUrl ? userContext?.userDetails?.first_name?.charAt(0) || 'C' : ''}
                />
              </a>
              <div className="dropdown-menu dropdown-primary" style={{ right: '80px' }}>
                <Menu model={profileMenu.filter((v) => v.show)} popup ref={profileRef} id="popup_menu" />
              </div>
            </div>

            <div
              className="mr-4 px-2 d-flex align-items-center"
              aria-controls="notification-panel"
              onClick={(e) => {
                notificationPanel.current.toggle(e);
                setNewNotification(false);

                // Reset notification count
                resetNotificationCount()
                  .then(() => {
                    setWebSocketNotificationsCount(0);
                    // Update notification list state to reset total_notifications
                    setNotifications((prev) => ({
                      ...prev,
                      total_notifications: 0,
                    }));
                    console.log('✅ [Client] Notification count reset successfully');
                  })
                  .catch((error) => {
                    console.error('❌ [Client] Error resetting notification count:', error);
                  });
              }}
            >
              <i className={newNotification ? 'icon-notification bell fa-lg me-1 pointer' : 'icon-notification fa-lg me-1 pointer'}></i>
              <Tooltip content="Notifications" target=".icon-notification" position="bottom" showDelay={500} />
              {totalNotiicationsCount ? <span className="p-badge">{totalNotiicationsCount ? totalNotiicationsCount : null}</span> : null}
            </div>
          </div>
        </div>
      </nav>

      <div className="content-container h-100 mt-0" style={{ height: dynamicHeight, overflow: 'auto' }}>
        <div className="container-fluid px-3 h-100">
          <div className="jumbotron px-1 admin-portal h-100 d-flex flex-column">
            <div className="flex-grow-1">
              <UserProfileContext.Provider value={userInfo}>{children}</UserProfileContext.Provider>
            </div>
            <footer className="version-footer mt-2">
              <label className="ms-2 F-size12">v{appVersion}</label>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout;
